import { InvoicesTable, QuotesTable } from '../../tables';
import { useAppContext } from '../../../context/AppContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatInvoiceStatus } from '../../../utils/billingTables';
const BILLING_TABS = {
    quotes: 'quotes',
    invoices: 'invoices',
};

export const BillingPage = () => {
    const {
        invoices,
        projects,
        quotes,
        setQuotes,
        isLoadingInvoices,
        isLoadingQuotes,
        user,
    } = useAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const isSuperAdmin = user?.is_super_admin === true;

    const tabFromUrl = searchParams.get('tab');
    const activeTab =
        tabFromUrl === BILLING_TABS.invoices ? BILLING_TABS.invoices : BILLING_TABS.quotes;

    const setActiveTab = useCallback(
        (tab) => {
            setSearchParams({ tab }, { replace: true });
        },
        [setSearchParams]
    );

    useEffect(() => {
        if (!tabFromUrl) {
            setSearchParams({ tab: BILLING_TABS.quotes }, { replace: true });
            return;
        }
        if (tabFromUrl !== BILLING_TABS.quotes && tabFromUrl !== BILLING_TABS.invoices) {
            setSearchParams({ tab: BILLING_TABS.quotes }, { replace: true });
        }
    }, [tabFromUrl, setSearchParams]);

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [stageFilter, setStageFilter] = useState('all');
    const [dueDateFilter, setDueDateFilter] = useState('');
    const [sortBy, setSortBy] = useState('newest');

    const [quoteSearchTerm, setQuoteSearchTerm] = useState('');
    const [quoteStatusFilter, setQuoteStatusFilter] = useState('all');
    const [quoteSortBy, setQuoteSortBy] = useState('newest');

    // Invoice filters (super admin) — moved from InvoicesPage
    const uniqueStatuses = useMemo(() => {
        const statuses = new Set();
        (invoices || []).forEach((invoice) => {
            if (invoice.invoice_status) {
                statuses.add(invoice.invoice_status);
            }
        });
        return Array.from(statuses).sort((a, b) => a.localeCompare(b));
    }, [invoices]);

    const projectStageById = useMemo(() => {
        const map = new Map();
        (projects || []).forEach((project) => {
            if (project?.id != null) {
                map.set(project.id, project.current_stage || '');
            }
        });
        return map;
    }, [projects]);

    const uniqueStages = useMemo(() => {
        const stages = new Set();
        (invoices || []).forEach((invoice) => {
            const stage = projectStageById.get(invoice.project_site_id);
            if (stage) {
                stages.add(stage);
            }
        });
        return Array.from(stages).sort((a, b) => a.localeCompare(b));
    }, [invoices, projectStageById]);

    const normalizeDate = (value) => {
        if (!value) return '';
        const rawValue = String(value).trim();
        const dateMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) return dateMatch[1];

        const parsed = new Date(rawValue);
        if (Number.isNaN(parsed.getTime())) return rawValue;
        return parsed.toISOString().split('T')[0];
    };

    const filteredInvoices = useMemo(() => {
        let filtered = invoices || [];

        if (!isSuperAdmin) {
            return filtered;
        }

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter((invoice) => {
                const projectName = (invoice.project_site_name || '').toLowerCase();
                const invoiceNumber = (invoice.invoice_number || '').toString().toLowerCase();
                return projectName.includes(searchLower) || invoiceNumber.includes(searchLower);
            });
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(
                (invoice) =>
                    (invoice.invoice_status || '').toLowerCase() === statusFilter.toLowerCase()
            );
        }

        if (stageFilter !== 'all') {
            filtered = filtered.filter(
                (invoice) => projectStageById.get(invoice.project_site_id) === stageFilter
            );
        }

        if (dueDateFilter) {
            filtered = filtered.filter(
                (invoice) => normalizeDate(invoice.invoice_due_date) === dueDateFilter
            );
        }

        return filtered;
    }, [invoices, isSuperAdmin, searchTerm, statusFilter, stageFilter, dueDateFilter, projectStageById]);

    const getRemainingBalance = (invoice) => {
        const value = Number(invoice?.remaining_balance);
        return Number.isNaN(value) ? 0 : value;
    };

    const getInvoiceTimestamp = (invoice) => {
        const dateValue = invoice?.created_at || invoice?.invoice_due_date || invoice?.updated_at || 0;
        const timestamp = new Date(dateValue).getTime();
        return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    const sortedInvoices = useMemo(() => {
        const sorted = [...filteredInvoices];

        if (sortBy === 'balance_high_to_low') {
            return sorted.sort((a, b) => getRemainingBalance(b) - getRemainingBalance(a));
        }

        if (sortBy === 'balance_low_to_high') {
            return sorted.sort((a, b) => getRemainingBalance(a) - getRemainingBalance(b));
        }

        if (sortBy === 'oldest') {
            return sorted.sort((a, b) => getInvoiceTimestamp(a) - getInvoiceTimestamp(b));
        }

        return sorted.sort((a, b) => getInvoiceTimestamp(b) - getInvoiceTimestamp(a));
    }, [filteredInvoices, sortBy]);

    const filteredQuotes = useMemo(() => {
        let filtered = quotes || [];

        if (!isSuperAdmin) {
            return filtered;
        }

        if (quoteSearchTerm) {
            const searchLower = quoteSearchTerm.toLowerCase();
            filtered = filtered.filter((quote) => {
                const projectName = (quote.project_site_name || '').toLowerCase();
                const quoteNumber = (quote.quote_number || '').toString().toLowerCase();
                return projectName.includes(searchLower) || quoteNumber.includes(searchLower);
            });
        }

        if (quoteStatusFilter === 'accepted') {
            filtered = filtered.filter((quote) => quote.is_accepted);
        } else if (quoteStatusFilter === 'pending') {
            filtered = filtered.filter((quote) => !quote.is_accepted);
        }

        return filtered;
    }, [quotes, isSuperAdmin, quoteSearchTerm, quoteStatusFilter]);

    const getQuoteTimestamp = (quote) => {
        const dateValue = quote?.created_at || quote?.updated_at || 0;
        const timestamp = new Date(dateValue).getTime();
        return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    const sortedQuotes = useMemo(() => {
        const sorted = [...filteredQuotes];

        if (quoteSortBy === 'oldest') {
            return sorted.sort((a, b) => getQuoteTimestamp(a) - getQuoteTimestamp(b));
        }

        return sorted.sort((a, b) => getQuoteTimestamp(b) - getQuoteTimestamp(a));
    }, [filteredQuotes, quoteSortBy]);

    const billingTabs = [
        { id: BILLING_TABS.quotes, label: 'Quotes', count: quotes?.length ?? 0 },
        { id: BILLING_TABS.invoices, label: 'Invoices', count: invoices?.length ?? 0 },
    ];

    const isLoading =
        activeTab === BILLING_TABS.invoices ? isLoadingInvoices : isLoadingQuotes;

    if (isLoading) {
        return (
            <div className="page">
                <div className="main-content">
                    <div className="page-header">
                        <h1>Billing</h1>
                        <hr />
                    </div>
                    <p>
                        {activeTab === BILLING_TABS.invoices
                            ? 'Loading invoices...'
                            : 'Loading quotes...'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="main-content">
                <div className="page-header">
                    <h1>Billing</h1>
                    <hr />
                </div>

                <div className="admin-tabs">
                    <div className="admin-tabs__list">
                        {billingTabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`admin-tab${activeTab === tab.id ? ' admin-tab--active' : ''}`}
                            >
                                {tab.label}
                                <span className={`admin-tab__count${activeTab === tab.id ? ' admin-tab__count--active' : ''}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {activeTab === BILLING_TABS.quotes && (
                    <>
                        {isSuperAdmin && (
                            <div className="admin-tools admin-tools--compact">
                                <h3>Search Tools</h3>
                                <div className="admin-tools-grid">
                                    <div className="form-group">
                                        <label htmlFor="quote-search" className="form-label">
                                            Search Quotes
                                        </label>
                                        <input
                                            id="quote-search"
                                            type="text"
                                            placeholder="Search by project name or quote number..."
                                            value={quoteSearchTerm}
                                            onChange={(e) => setQuoteSearchTerm(e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="quote-status-filter" className="form-label">
                                            Filter by Status
                                        </label>
                                        <select
                                            id="quote-status-filter"
                                            value={quoteStatusFilter}
                                            onChange={(e) => setQuoteStatusFilter(e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="all">All Statuses</option>
                                            <option value="pending">Pending</option>
                                            <option value="accepted">Accepted</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="quote-sort" className="form-label">
                                            Sort By
                                        </label>
                                        <select
                                            id="quote-sort"
                                            value={quoteSortBy}
                                            onChange={(e) => setQuoteSortBy(e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="newest">Newest to Oldest</option>
                                            <option value="oldest">Oldest to Newest</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setQuoteSearchTerm('');
                                                setQuoteStatusFilter('all');
                                                setQuoteSortBy('newest');
                                            }}
                                            className="btn btn-secondary btn--block"
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                                <p className="admin-tools-summary admin-tools-summary--bare" aria-live="polite">
                                    Showing {filteredQuotes.length} of {quotes?.length || 0} quotes
                                    {quoteSearchTerm && ` (search: "${quoteSearchTerm}")`}
                                    {quoteStatusFilter !== 'all' && ` (status: ${quoteStatusFilter})`}
                                </p>
                            </div>
                        )}
                        <QuotesTable
                            quotes={sortedQuotes}
                            setQuotes={setQuotes}
                            showProjectColumn
                            highlightPending={isSuperAdmin}
                        />
                    </>
                )}

                {activeTab === BILLING_TABS.invoices && (
                    <>
                        {isSuperAdmin && (
                            <div className="admin-tools admin-tools--compact">
                                <h3>Search Tools</h3>
                                <div className="admin-tools-grid">
                                    <div className="form-group">
                                        <label htmlFor="invoice-search" className="form-label">
                                            Search Invoices
                                        </label>
                                        <input
                                            id="invoice-search"
                                            type="text"
                                            placeholder="Search by project name or invoice number..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="invoice-status-filter" className="form-label">
                                            Filter by Status
                                        </label>
                                        <select
                                            id="invoice-status-filter"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="all">All Statuses</option>
                                            {uniqueStatuses.map((status) => (
                                                <option key={status} value={status}>
                                                    {formatInvoiceStatus(status)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="invoice-stage-filter" className="form-label">
                                            Filter by Project Stage
                                        </label>
                                        <select
                                            id="invoice-stage-filter"
                                            value={stageFilter}
                                            onChange={(e) => setStageFilter(e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="all">All Stages</option>
                                            {uniqueStages.map((stage) => (
                                                <option key={stage} value={stage}>
                                                    {stage}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="invoice-due-date-filter" className="form-label">
                                            Filter by Due Date
                                        </label>
                                        <input
                                            id="invoice-due-date-filter"
                                            type="date"
                                            value={dueDateFilter}
                                            onChange={(e) => setDueDateFilter(e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="invoice-sort" className="form-label">
                                            Sort By
                                        </label>
                                        <select
                                            id="invoice-sort"
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="newest">Newest to Oldest</option>
                                            <option value="oldest">Oldest to Newest</option>
                                            <option value="balance_high_to_low">Balance: Highest to Lowest</option>
                                            <option value="balance_low_to_high">Balance: Lowest to Highest</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchTerm('');
                                                setStatusFilter('all');
                                                setStageFilter('all');
                                                setDueDateFilter('');
                                                setSortBy('newest');
                                            }}
                                            className="btn btn-secondary btn--block"
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                                <p className="admin-tools-summary admin-tools-summary--bare" aria-live="polite">
                                    Showing {filteredInvoices.length} of {invoices?.length || 0} invoices
                                    {searchTerm && ` (search: "${searchTerm}")`}
                                    {statusFilter !== 'all' && ` (status: ${formatInvoiceStatus(statusFilter)})`}
                                    {stageFilter !== 'all' && ` (stage: ${stageFilter})`}
                                    {dueDateFilter && ` (due: ${dueDateFilter})`}
                                </p>
                            </div>
                        )}
                        <InvoicesTable invoices={sortedInvoices} highlightUnpaid={isSuperAdmin} />
                    </>
                )}
            </div>
        </div>
    );
};
